import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { Close, Visibility } from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { dispatchTicketUpdated } from "../../utils/ticketEvents";
import {
	getStoredPrivacyCensorSettings,
	privacyCensorChangedEvent,
} from "../../utils/privacyCensor";
import {
	getConnectionChannelColor,
	getConnectionChannelIcon,
} from "../ConnectionChannelIcon";
import MessagesList from "../MessagesList";

const getReadableTextColor = color => {
	const hex = (color || "#ffffff").replace("#", "");
	const normalized =
		hex.length === 3
			? hex.split("").map(char => `${char}${char}`).join("")
			: hex.padEnd(6, "f").slice(0, 6);
	const red = parseInt(normalized.slice(0, 2), 16);
	const green = parseInt(normalized.slice(2, 4), 16);
	const blue = parseInt(normalized.slice(4, 6), 16);
	const luminance = (red * 299 + green * 587 + blue * 114) / 1000;

	return luminance > 150 ? "#243040" : "#ffffff";
};

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
	},

	pendingTicket: {
		cursor: "unset",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
	},

	lastMessageTime: {
		justifySelf: "flex-end",
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		paddingRight: 160,
	},
	censoredPhoto: {
		filter: "blur(10px)",
		transform: "scale(1.04)",
		borderRadius: "50%",
		overflow: "hidden",
	},
	censoredText: {
		padding: "1px 8px",
		filter: "blur(3px)",
		userSelect: "none",
	},

	labelRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
		marginTop: 2,
		marginBottom: 2,
	},

	labelBadge: {
		display: "inline-flex",
		alignItems: "center",
		maxWidth: 112,
		height: "auto",
		minHeight: 24,
		padding: "2px 10px",
		borderRadius: 12,
		fontSize: "0.8125rem",
		fontWeight: 500,
		lineHeight: 1.4,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		border: "1px solid rgba(0, 0, 0, 0.08)",
	},

	newMessagesCount: {
		"& .MuiBadge-badge": {
			color: "white",
			backgroundColor: green[500],
		},
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	acceptButton: {
		minWidth: 82,
	},

	pendingActions: {
		position: "absolute",
		left: "50%",
		display: "flex",
		alignItems: "center",
		gap: 6,
		zIndex: 2,
	},

	previewButton: {
		width: 34,
		height: 30,
		padding: 0,
	},

	previewDialogPaper: {
		height: "82vh",
	},

	previewDialogTitle: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(1, 2),
	},

	previewDialogContent: {
		padding: 0,
		display: "flex",
		height: "100%",
	},

	ticketQueueColor: {
		flex: "none",
		width: "8px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
	},

	ticketMetaTags: {
		position: "absolute",
		right: 5,
		bottom: 5,
		display: "flex",
		alignItems: "center",
		gap: 4,
		maxWidth: 320,
	},

	userTag: {
		maxWidth: 112,
		background: "#2576D2",
		color: "#ffffff",
		border: "1px solid #CCC",
		height: "auto",
		minHeight: 24,
		padding: "2px 10px",
		borderRadius: 12,
		fontSize: "0.8125rem",
		fontWeight: 500,
		lineHeight: 1.4,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},

	tagWithAvatar: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		paddingLeft: 4,
	},

	tagAvatar: {
		width: 20,
		height: 20,
		fontSize: 12,
	},

	connectionAvatar: {
		width: 20,
		height: 20,
		backgroundColor: "#ffffff",
	},

	connectionIcon: {
		width: 18,
		height: 18,
		display: "block",
	},

	tagText: {
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},

	agentTag: {
		background: "#455A64",
	},

	queueTag: {
		border: "1px solid rgba(0, 0, 0, 0.08)",
	},

	avatarBadge: {
		"& .MuiBadge-badge": {
			top: 4,
			right: 4,
			color: "white",
			backgroundColor: green[500],
			border: "2px solid #fff",
			minWidth: 18,
			height: 18,
			fontSize: 11,
			padding: "0 5px",
		},
	},

	avatarSelectionWrapper: {
		position: "relative",
		width: 40,
		height: 40,
		flex: "0 0 40px",
	},

	avatarSelection: {
		position: "absolute",
		top: 0,
		left: 0,
		width: 40,
		height: 40,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "rgba(255, 255, 255, 0.72)",
		borderRadius: "50%",
		zIndex: 2,
	},

	avatarSelectionCheckbox: {
		padding: 0,
		width: 32,
		height: 32,
	},
}));

const TicketListItem = ({
	ticket,
	selectionMode = false,
	selected = false,
	onToggleSelection = () => {},
}) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const [privacyCensorSettings, setPrivacyCensorSettings] = useState(() =>
		getStoredPrivacyCensorSettings(user?.id)
	);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		setPrivacyCensorSettings(getStoredPrivacyCensorSettings(user?.id));
	}, [user?.id]);

	useEffect(() => {
		const handlePrivacyCensorChanged = event => {
			setPrivacyCensorSettings(
				event.detail || getStoredPrivacyCensorSettings(user?.id)
			);
		};

		window.addEventListener(
			privacyCensorChangedEvent,
			handlePrivacyCensorChanged
		);

		return () => {
			window.removeEventListener(
				privacyCensorChangedEvent,
				handlePrivacyCensorChanged
			);
		};
	}, [user?.id]);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			const { data } = await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
			dispatchTicketUpdated(data);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	const handleOpenPreview = event => {
		event.stopPropagation();
		setPreviewOpen(true);
	};

	const handleClosePreview = event => {
		event?.stopPropagation?.();
		setPreviewOpen(false);
	};

	const connectionChannel = ticket.connection?.channel || "connection";
	const ConnectionIcon = getConnectionChannelIcon(connectionChannel);
	const connectionColor = getConnectionChannelColor(connectionChannel);
	const lastMessageDate = ticket.lastMessageAt || ticket.updatedAt;
	const showPreviewAction = ticket.status === "pending" || ticket.status === "open";
	const showTicketMetaTags =
		!privacyCensorSettings.ticketMetaTags &&
		(ticket.queue?.name || ticket.user?.name || ticket.connectionId);

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={e => {
					if (e.shiftKey || e.ctrlKey || e.metaKey) {
						e.preventDefault();
						onToggleSelection(ticket, e);
						return;
					}
					if (ticket.status === "pending") return;
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
				})}
			>
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Sem fila"}
				>
					<span
						style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>
				<ListItemAvatar>
					<div className={classes.avatarSelectionWrapper}>
						<Badge
							className={classes.avatarBadge}
							badgeContent={selectionMode ? 0 : ticket.unreadMessages}
							classes={{ badge: classes.badgeStyle }}
							overlap="circle"
						>
							<Avatar
								src={
									privacyCensorSettings.contactPhotos
										? undefined
										: ticket?.contact?.profilePicUrl
								}
								className={clsx({
									[classes.censoredPhoto]:
										privacyCensorSettings.contactPhotos,
								})}
							/>
						</Badge>
						{selectionMode && (
							<div
								className={classes.avatarSelection}
								onClick={event => {
									event.stopPropagation();
									onToggleSelection(ticket, event);
								}}
							>
								<Checkbox
									color="primary"
									checked={selected}
									onChange={() => {}}
									className={classes.avatarSelectionCheckbox}
								/>
							</div>
						)}
					</div>
				</ListItemAvatar>
				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
							>
								<span
									className={clsx({
										[classes.censoredText]:
											privacyCensorSettings.contactNames,
									})}
								>
									{privacyCensorSettings.contactNames
										? "Contacto privado"
										: ticket.contact.name}
								</span>
							</Typography>
							{ticket.status === "closed" && (
								<Badge
									className={classes.closedBadge}
									badgeContent={"closed"}
									color="primary"
								/>
							)}
							{ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{isSameDay(parseISO(lastMessageDate), new Date()) ? (
										<>{format(parseISO(lastMessageDate), "HH:mm")}</>
									) : (
										<>{format(parseISO(lastMessageDate), "dd/MM/yyyy")}</>
									)}
								</Typography>
							)}
							{showTicketMetaTags && (
								<div className={classes.ticketMetaTags}>
									{ticket.queue?.name && (
										<div
											className={clsx(classes.userTag, classes.queueTag)}
											title="Área"
											style={{
												backgroundColor: ticket.queue.color || "#7C7C7C",
												color: getReadableTextColor(ticket.queue.color || "#7C7C7C"),
											}}
										>
											{ticket.queue.name}
										</div>
									)}
									{ticket.user?.name && (
										<div
											className={clsx(
												classes.userTag,
												classes.agentTag,
												classes.tagWithAvatar
											)}
											title={i18n.t("ticketsList.agentTitle")}
										>
											<Avatar
												src={ticket.user.profilePicUrl}
												className={classes.tagAvatar}
											>
												{ticket.user.name?.[0]}
											</Avatar>
											<span className={classes.tagText}>{ticket.user.name}</span>
										</div>
									)}
									{ticket.connectionId && (
										<div
											className={clsx(
												classes.userTag,
												classes.tagWithAvatar
											)}
											title={i18n.t("ticketsList.connectionTitle")}
											style={{ backgroundColor: connectionColor }}
										>
											<Avatar className={classes.connectionAvatar}>
												<ConnectionIcon className={classes.connectionIcon} />
											</Avatar>
											<span className={classes.tagText}>
												{ticket.connection?.name}
											</span>
										</div>
									)}
								</div>
							)}
						</span>
					}
					secondary={
						<>
							{Boolean(ticket.labels?.length) && (
								<span className={classes.labelRow}>
									{ticket.labels.map(label => (
										<span
											key={label.id}
											title={label.name}
											className={classes.labelBadge}
											style={{
												backgroundColor: label.color,
												color: getReadableTextColor(label.color),
											}}
										>
											{label.name}
										</span>
									))}
								</span>
							)}
							<span className={classes.contactNameWrapper}>
								<Typography
									className={classes.contactLastMessage}
									noWrap
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{ticket.lastMessage ? (
										<span
											className={clsx({
												[classes.censoredText]:
													privacyCensorSettings.contactNames,
											})}
										>
											{privacyCensorSettings.contactNames ? (
												"Mensaje privado"
											) : (
												<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
											)}
										</span>
									) : (
										<br />
									)}
								</Typography>
							</span>
						</>
					}
				/>
				{showPreviewAction && (
					<div className={classes.pendingActions}>
						<Tooltip title="Ver historial">
							<IconButton
								color="primary"
								className={classes.previewButton}
								size="small"
								onClick={handleOpenPreview}
							>
								<Visibility fontSize="small" />
							</IconButton>
						</Tooltip>
						{ticket.status === "pending" && (
							<ButtonWithSpinner
								color="primary"
								variant="contained"
								className={classes.acceptButton}
								size="small"
								loading={loading}
								onClick={e => {
									e.stopPropagation();
									handleAcepptTicket(ticket.id);
								}}
							>
								{i18n.t("ticketsList.buttons.accept")}
							</ButtonWithSpinner>
						)}
					</div>
				)}
			</ListItem>
			<Dialog
				open={previewOpen}
				onClose={handleClosePreview}
				maxWidth="md"
				fullWidth
				classes={{ paper: classes.previewDialogPaper }}
			>
				<DialogTitle disableTypography className={classes.previewDialogTitle}>
					<Typography variant="h6" noWrap>
						{ticket.contact?.name || "Historial de conversación"}
					</Typography>
					<IconButton size="small" onClick={handleClosePreview}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent className={classes.previewDialogContent}>
					{previewOpen && (
						<MessagesList
							ticketId={ticket.id}
							isGroup={Boolean(ticket.isGroup || ticket.contact?.isGroup)}
							readOnly
						/>
					)}
				</DialogContent>
			</Dialog>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;

